import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Check, Play, Square, Loader2 } from "lucide-react";

export const VoicePicker = ({ value, onChange }) => {
  const [voices, setVoices] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => { api.listVoices().then(setVoices).catch(() => {}); return () => audioRef.current?.pause(); }, []);

  const preview = (e, key) => {
    e.stopPropagation();
    audioRef.current?.pause();
    if (previewing === key) return setPreviewing(null);
    const a = new Audio(`${API}/voices/${key}/preview`);
    audioRef.current = a;
    setPreviewing(`${key}:loading`);
    a.oncanplay = () => setPreviewing(key);
    a.onended = () => setPreviewing(null);
    a.onerror = () => setPreviewing(null);
    a.play().catch(() => setPreviewing(null));
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="voice-picker">
      {voices.map((v) => {
        const active = value === v.key;
        const state = previewing === v.key ? "playing" : previewing === `${v.key}:loading` ? "loading" : null;
        return (
          <div
            key={v.key}
            role="button"
            tabIndex={0}
            data-testid={`voice-option-${v.key}`}
            onClick={() => onChange(v.key)}
            onKeyDown={(e) => e.key === "Enter" && onChange(v.key)}
            className={`relative rounded-xl p-3 border cursor-pointer transition-colors ${active ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-1.5">
                {active && <Check className="h-3.5 w-3.5 text-[#F59E0B]" strokeWidth={3} />}{v.label}
              </span>
              <button
                type="button"
                data-testid={`voice-preview-${v.key}`}
                onClick={(e) => preview(e, v.key)}
                title="Hear a sample"
                className="h-7 w-7 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] flex items-center justify-center hover:bg-[#F59E0B]/35 transition-colors"
              >
                {state === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : state === "playing" ? <Square className="h-3 w-3" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
              </button>
            </div>
            <div className="text-xs text-[#94A3B8] mt-1 leading-snug">{v.desc}</div>
          </div>
        );
      })}
    </div>
  );
};
